import { useEffect, useMemo, useRef, type RefObject } from 'react';
import type { SourceName, StreamRef, TvDetails } from '@/types/streaming';
import { prefetchStream } from '@/services/streamingApi';
import { resolveNextEpisode } from '@/components/resolveNextEpisode';

/** HTMLMediaElement.HAVE_FUTURE_DATA — enough buffered to advance at least one
 *  frame past the current position, i.e. steady-state playback rather than an
 *  empty/stalled buffer. We treat this (and no active seek) as "healthy". */
const HAVE_FUTURE_DATA = 3;

export interface ShouldPrefetchNextArgs {
  /** currentTime has entered the last `leadSeconds` of the episode. */
  inLeadWindow: boolean;
  /** Playback is buffered + advancing, not stalled or mid-seek. */
  playbackHealthy: boolean;
  /** This (episode, source) already fired its one prefetch. */
  alreadyFired: boolean;
  /** A next episode actually exists to warm. */
  hasNext: boolean;
}

/**
 * Pure decision for the next-episode prefetch: fire only when the viewer is in
 * the lead window, playback is healthy, we haven't already fired for this
 * (episode, source), and there is a next episode to warm. Exported for direct
 * unit testing of each condition.
 */
export function shouldPrefetchNext({
  inLeadWindow,
  playbackHealthy,
  alreadyFired,
  hasNext,
}: ShouldPrefetchNextArgs): boolean {
  return inLeadWindow && playbackHealthy && !alreadyFired && hasNext;
}

export interface UseNextEpisodePrefetchArgs {
  videoRef: RefObject<HTMLVideoElement>;
  /** The currently-playing ref. Movies/anime are no-ops (TV only). */
  streamRef: StreamRef;
  /** TV details for the current title; null while loading or for non-TV. */
  details: TvDetails | null;
  /** Pinned source (`?source=`) or null for Auto — keys the fire-once ref. */
  activeSource: SourceName | null;
  /** Lead window before end-of-episode, in seconds (nextEpisodeLeadSeconds). */
  leadSeconds: number;
  /** Test seam. Defaults to the real backend-resolve-cache warmer. */
  prefetch?: (ref: StreamRef, source: SourceName | undefined, signal: AbortSignal) => void | Promise<void>;
}

/**
 * TV-only: warm the NEXT episode's backend resolve (the expensive provider-chain
 * walk) BEFORE the viewer clicks Next, so the click only pays for the playlist
 * GET. Fires exactly once per (episode, source) the moment playback enters the
 * lead window while healthy; the in-flight prefetch is aborted on unmount and on
 * any episode/source change so a fast skip never leaves a stale request running.
 *
 * The prefetch itself is abortable and fire-and-forget (`prefetchStream` swallows
 * its own errors and the backend 204s even on provider failure), so it never
 * blocks or perturbs current playback. Movies and anime are no-ops — there is no
 * "next episode" resolution for them.
 */
export function useNextEpisodePrefetch({
  videoRef,
  streamRef,
  details,
  activeSource,
  leadSeconds,
  prefetch = prefetchStream,
}: UseNextEpisodePrefetchArgs): void {
  const isTv = streamRef.type === 'tv';
  const tmdbId = streamRef.type === 'tv' ? streamRef.tmdbId : null;
  const season = streamRef.type === 'tv' ? streamRef.season : null;
  const episode = streamRef.type === 'tv' ? streamRef.episode : null;

  // Resolve on primitive deps so a new `details` object identity with unchanged
  // season content doesn't churn the effect below. The effect keys off the next
  // episode's primitive numbers, not this object's identity.
  const next = useMemo(
    () => (details && season != null && episode != null
      ? resolveNextEpisode(details.seasons, season, episode)
      : null),
    [details, season, episode],
  );
  const nextSeason = next?.season ?? null;
  const nextEpisode = next?.episode ?? null;

  // Fire-once guard, reset whenever the effect re-arms (episode/source/next
  // change). A re-render that changes nothing relevant does NOT reset it.
  const firedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    firedRef.current = false;
    const video = videoRef.current;
    if (!isTv || tmdbId == null || nextSeason == null || nextEpisode == null || !video) {
      return undefined;
    }

    const nextRef: StreamRef = { type: 'tv', tmdbId, season: nextSeason, episode: nextEpisode };

    function evaluate(): void {
      if (!video || firedRef.current) return;
      const { currentTime, duration, seeking, readyState } = video;
      const inLeadWindow = Number.isFinite(duration) && duration > 0
        && currentTime >= duration - leadSeconds;
      const playbackHealthy = !seeking && readyState >= HAVE_FUTURE_DATA;
      if (!shouldPrefetchNext({ inLeadWindow, playbackHealthy, alreadyFired: firedRef.current, hasNext: true })) {
        return;
      }
      firedRef.current = true;
      const controller = new AbortController();
      abortRef.current = controller;
      void prefetch(nextRef, activeSource ?? undefined, controller.signal);
    }

    video.addEventListener('timeupdate', evaluate);
    return () => {
      video.removeEventListener('timeupdate', evaluate);
      // Cancel any in-flight prefetch when the episode/source changes or the
      // player unmounts — a stale warm for an episode the viewer skipped past
      // is wasted work.
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [videoRef, isTv, tmdbId, nextSeason, nextEpisode, activeSource, leadSeconds, prefetch]);
}
